import "./global.css";
import "@/i18n";

import { createRoot } from "react-dom/client";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import Manuals from "./pages/Manuals";
import Settings from "./pages/Settings";
import CRM from "./pages/CRM";
import DashboardPage from "./pages/DashboardPage";
import Deal from "./pages/Deal";
import Measure from "./pages/Measure";
import Handover from "./pages/Handover";
import LossReasons from "./pages/LossReasons";
const RouteGenerator = lazy(() => import("./pages/RouteGenerator"));
import Images from "./pages/Images";
import ImagesFolder from "./pages/ImagesFolder";
import ImagesRawGallery from "./pages/ImagesRawGallery";
import ShoppingList from "./pages/ShoppingList";
import Hivogatas from "./pages/Hivogatas";
import ContactsCallLog from "./pages/ContactsCallLog";
import Contacts from "./pages/Contacts";
import ContactDetail from "./pages/ContactDetail";
import Layout from "@/components/Layout";
import { AuthLiteProvider } from "@/context/AuthLite";
import { ThemeProvider } from "@/components/ThemeProvider";
import { LocaleSyncFromUser } from "@/components/LocaleSyncFromUser";
import { useEffect } from "react";
import { logDistanceServiceStatus } from "@/lib/distance-service-google";
import { useKeepRenderAlive } from "@/hooks/useKeepRenderAlive";
// Debug: track notes API calls (development only)
if (import.meta.env.DEV) {
  import("@/lib/debug-notes-fetching");
}
// Debug panel removed - not needed
// import NotesFetchDebugPanel from "@/components/debug/NotesFetchDebugPanel";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import PWAUpdatePrompt from "@/components/PWAUpdatePrompt";
import Offline from "./pages/Offline";
import EmbedConfigurator from "./pages/EmbedConfigurator";
import EmbedAccessConfigurator from "./pages/EmbedAccessConfigurator";
import { useIsMobileState } from "@/hooks/use-mobile";
import Loading from "@/components/Loading";
import { useAuthLite } from "@/context/AuthLite";
import i18n from "@/i18n";
import ConfiguratorLog from "./pages/ConfiguratorLog";
import { ImageUploadOutboxBootstrap } from "@/components/ImageUploadOutboxBootstrap";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Initialize distance service and keep Render service awake on app load
function AppInitializer() {
  useEffect(() => {
    logDistanceServiceStatus();
  }, []);

  // Keep Render IMAP sync service awake while app is open
  useKeepRenderAlive();

  return null;
}

// Mobile route guard - redirects mobile users away from route generator (not used for price-generator: mobil Log)
function MobileRouteGuard({ children }: { children: React.ReactNode }) {
  const isMobile = useIsMobileState();
  // Don't redirect until we know viewport (avoid flash redirect on mobile)
  if (isMobile === undefined) return <>{children}</>;
  if (isMobile) return <Navigate to="/crm" replace />;
  return <>{children}</>;
}

// Mobile-only route guard - redirects desktop users away from Measure (Bemérés)
function MobileOnlyRouteGuard({ children }: { children: React.ReactNode }) {
  const isMobile = useIsMobileState();
  // Don't redirect until we know viewport (fix: was redirecting on first paint because useIsMobile was false before hydration)
  if (isMobile === undefined) return <>{children}</>;
  if (!isMobile) return <Navigate to="/crm" replace />;
  return <>{children}</>;
}

// When authMode is "email", redirect to /login if not authenticated
function ProtectedLayoutGuard({ children }: { children: React.ReactNode }) {
  const { user, authMode } = useAuthLite();
  if (authMode === "email" && !user) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

// Only Admin can access; others are redirected to CRM
function AdminRouteGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthLite();
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        {i18n.t("common.loading")}
      </div>
    );
  }
  if (!user || user.role !== "Admin") {
    return <Navigate to="/crm" replace />;
  }
  return <>{children}</>;
}

function AppContent() {
  const { isLoading } = useAuthLite();

  if (isLoading) {
    return <Loading message={i18n.t("common.appLoading")} className="min-h-screen" />;
  }

  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/offline" element={<Offline />} />
        <Route path="/embed/configurator" element={<EmbedConfigurator />} />
        <Route path="/hu/embed/configurator" element={<EmbedConfigurator />} />
        <Route path="/embed/access-configurator" element={<EmbedAccessConfigurator />} />
        <Route path="/hu/embed/access-configurator" element={<EmbedAccessConfigurator />} />
        <Route
          path="*"
          element={
            <ProtectedLayoutGuard>
              <Layout>
                <ErrorBoundary>
                  <Routes>
                    <Route path="/" element={<Navigate to="/crm" replace />} />
                    <Route path="/manuals" element={<Manuals />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/settings/:section/:subsection" element={<Settings />} />
                    <Route path="/settings/:section" element={<Settings />} />
                    <Route path="/admin/mappings" element={<Navigate to="/settings" replace />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/crm" element={<CRM />} />
                    <Route path="/crm/deals/:id" element={<Deal />} />
                    <Route
                      path="/admin/loss-reasons"
                      element={
                        <AdminRouteGuard>
                          <LossReasons />
                        </AdminRouteGuard>
                      }
                    />
                    <Route
                      path="/crm/measure"
                      element={
                        <MobileOnlyRouteGuard>
                          <Measure />
                        </MobileOnlyRouteGuard>
                      }
                    />
                    <Route
                      path="/crm/measure/:id"
                      element={
                        <MobileOnlyRouteGuard>
                          <Measure />
                        </MobileOnlyRouteGuard>
                      }
                    />
                    <Route
                      path="/crm/handover"
                      element={
                        <MobileOnlyRouteGuard>
                          <Handover />
                        </MobileOnlyRouteGuard>
                      }
                    />
                    <Route
                      path="/crm/handover/:id"
                      element={
                        <MobileOnlyRouteGuard>
                          <Handover />
                        </MobileOnlyRouteGuard>
                      }
                    />
                    <Route
                      path="/admin/route-generator"
                      element={
                        <MobileRouteGuard>
                          <Suspense fallback={<div className="flex items-center justify-center h-screen">{i18n.t("common.loading")}</div>}>
                            <RouteGenerator />
                          </Suspense>
                        </MobileRouteGuard>
                      }
                    />
                    <Route
                      path="/admin/price-generator"
                      element={
                        <AdminRouteGuard>
                          <Navigate to="/settings/price-generator" replace />
                        </AdminRouteGuard>
                      }
                    />
                    <Route
                      path="/admin/price-generator/log"
                      element={
                        <AdminRouteGuard>
                          <ConfiguratorLog />
                        </AdminRouteGuard>
                      }
                    />
                    <Route path="/images/projects" element={<Images />} />
                    <Route path="/images/gallery" element={<ImagesRawGallery />} />
                    <Route path="/images/maps" element={<ImagesRawGallery />} />
                    <Route path="/images" element={<Navigate to="/images/gallery" replace />} />
                    <Route path="/images/:folderId" element={<ImagesFolder />} />
                    <Route path="/shopping-list" element={<ShoppingList />} />
                    <Route path="/hivogatas" element={<Navigate to="/contacts/calling" replace />} />
                    <Route path="/contacts/calling" element={<Hivogatas />} />
                    <Route path="/contacts/call-logs" element={<ContactsCallLog />} />
                    <Route path="/contacts" element={<Contacts />} />
                    <Route path="/contacts/:id" element={<ContactDetail />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </ErrorBoundary>
              </Layout>
            </ProtectedLayoutGuard>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

const App = () => (
  <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <AppInitializer />
      <ImageUploadOutboxBootstrap />
      <TooltipProvider>
        <Sonner />
        <ErrorBoundary>
          <AuthLiteProvider>
            <LocaleSyncFromUser />
            <AppContent />
            {/* Debug panel removed */}
            {/* <NotesFetchDebugPanel /> */}
            <PWAInstallPrompt />
            <PWAUpdatePrompt />
          </AuthLiteProvider>
        </ErrorBoundary>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

createRoot(document.getElementById("root")!).render(<App />);
